import { useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, Trash2, CheckCheck, Loader2 } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const FillItem = ({ kitId, n, f, reload }) => {
  const [edit, setEdit] = useState(false);
  const [heading, setHeading] = useState(f.heading);
  const [text, setText] = useState(f.paragraphs.join("\n\n"));
  const [busy, setBusy] = useState(false);
  const call = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setEdit(false); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const save = () => call(() => http.put(`/kits/${kitId}/original-fill/${n}`, { heading, paragraphs: text.split(/\n\s*\n/) }), "Saved and kept");
  const keep = () => call(() => http.put(`/kits/${kitId}/original-fill/${n}`, {}), "Kept");
  const remove = () => call(() => http.delete(`/kits/${kitId}/original-fill/${n}`), "Removed — that page stays as you wrote it");
  const ib = "inline-flex items-center gap-1 rounded-sm px-2.5 py-1 text-xs font-bold transition-colors disabled:opacity-50";
  return (
    <li data-testid={`fill-item-${n}`} className="border-t border-stone-200 py-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-stone-500">
        <span>Page {Number(n) + 1} · {f.title}</span>
        <span data-testid={`fill-status-${n}`} className={`rounded-sm px-1.5 py-0.5 font-bold ${f.status === "kept" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{f.status === "kept" ? "Kept" : "To review"}</span>
        <div className="ml-auto flex gap-1.5">
          {f.status !== "kept" && !edit && <button data-testid={`fill-keep-${n}`} disabled={busy} onClick={keep} className={`${ib} bg-[#2B2A26] text-white hover:bg-black`}><Check size={12} /> Keep</button>}
          {!edit && <button data-testid={`fill-edit-${n}`} disabled={busy} onClick={() => setEdit(true)} className={`${ib} border border-stone-300 bg-white hover:border-[#9A7B2C]`}><Pencil size={12} /> Edit</button>}
          <button data-testid={`fill-remove-${n}`} disabled={busy} onClick={remove} className={`${ib} text-red-700 hover:bg-red-50`}><Trash2 size={12} /> Remove</button>
        </div>
      </div>
      {edit ? (
        <div className="mt-2 space-y-2">
          <input data-testid={`fill-heading-${n}`} value={heading} onChange={(e) => setHeading(e.target.value)} className="w-full rounded-sm border border-stone-300 bg-white px-2 py-1.5 text-sm font-semibold" />
          <textarea data-testid={`fill-text-${n}`} value={text} onChange={(e) => setText(e.target.value)} rows={7} className="w-full rounded-sm border border-stone-300 bg-white px-2 py-1.5 text-sm leading-relaxed" />
          <p className="text-xs text-stone-500">Leave a blank line between paragraphs.</p>
          <div className="flex gap-2">
            <button data-testid={`fill-save-${n}`} disabled={busy} onClick={save} className={`${ib} bg-[#2B2A26] text-white hover:bg-black`}>{busy && <Loader2 size={12} className="animate-spin" />} Save &amp; keep</button>
            <button onClick={() => setEdit(false)} className={`${ib} text-stone-500 hover:text-black`}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="mt-2">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#7B2D3B]">{f.heading}</p>
          {f.paragraphs.map((p, i) => <p key={i} className="mt-1 text-sm leading-relaxed text-stone-700">{p}</p>)}
        </div>
      )}
    </li>
  );
};

export const FillReview = ({ kit, reload }) => {
  const fill = kit.original_fill || {};
  const keys = Object.keys(fill).sort((a, b) => Number(a) - Number(b));
  const toReview = keys.filter((k) => fill[k].status !== "kept").length;
  const [busy, setBusy] = useState(false);
  if (!keys.length) return null;
  const keepAll = async () => {
    setBusy(true);
    try { await http.post(`/kits/${kit.id}/original-fill-keep-all`); toast.success("All added sub-sections kept"); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div data-testid="fill-review" className="mt-4 rounded-md border border-stone-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <b className="text-sm text-[#2B2A26]">Added to light pages</b>
        <span data-testid="fill-review-count" className="text-xs text-stone-500">{keys.length} sub-section(s){toReview ? ` · ${toReview} to review before publishing` : " · all reviewed"}</span>
        {toReview > 0 && <button data-testid="fill-keep-all" disabled={busy} onClick={keepAll} className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-[#9A7B2C] hover:text-black"><CheckCheck size={13} /> Keep all</button>}
      </div>
      <ul className="mt-2 max-h-[32rem] overflow-y-auto pr-1">{keys.map((k) => <FillItem key={`${k}-${fill[k].status}-${fill[k].heading}`} kitId={kit.id} n={k} f={fill[k]} reload={reload} />)}</ul>
    </div>
  );
};
